import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { authApi, userApi, userCartApi } from '../services/api';
import { useToast } from './ToastContext';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const { addToast } = useToast();
  
  // Prevent duplicate profile requests
  const profileRequestRef = React.useRef(null);

  // Initialize authentication state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const token = authApi.getToken();
        
        if (token) {
          // Token exists, try to fetch user profile
          try {
            const profile = await userApi.getProfile();
            setUser(profile);
            setIsAuthenticated(true);
            
            // Set token for API requests
            authApi.setToken(token);
          } catch (error) {
            // Token is invalid or expired
            console.error('Failed to fetch user profile:', error);
            authApi.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
        setInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      
      // Include sessionId for cart merging if available
      const sessionId = localStorage.getItem('cartSessionId') || 
                       sessionStorage.getItem('cartSessionId') || 
                       Date.now().toString();
      
      const loginData = { ...credentials, sessionId };
      const response = await authApi.login(loginData);
      
      if (response.token) {
        // Fetch user profile after successful login
        const profile = await userApi.getProfile();
        setUser(profile);
        setIsAuthenticated(true);
        
        addToast('Başarıyla giriş yaptınız!', 'success');
        
        // Clear any stored session ID as cart should now be merged
        localStorage.removeItem('cartSessionId');
        sessionStorage.removeItem('cartSessionId');
        
        // Redirect to intended page if stored
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath && redirectPath !== '/login' && redirectPath !== '/register') {
          localStorage.removeItem('redirectAfterLogin');
          window.location.href = redirectPath;
        }
        
        return { success: true, user: profile };
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';
      addToast(errorMessage, 'error');
      
      setUser(null);
      setIsAuthenticated(false);
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      const response = await authApi.register(userData);
      
      // Registration successful if we get any response without error
      if (response) {
        addToast('Hesabınız başarıyla oluşturuldu! Giriş yapmak için e-posta adresinizi doğrulamanız gerekebilir.', 'success');
        
        // Show additional info about next steps
        setTimeout(() => {
          addToast('Lütfen giriş sayfasından bilgilerinizle oturum açın.', 'info', 6000);
        }, 2000);
        
        return { success: true, requiresLogin: true };
      } else {
        throw new Error('Kayıt başarısız');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Kayıt başarısız. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Logout function
  const logout = useCallback(() => {
    try {
      authApi.logout();
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any user-related data from localStorage
      localStorage.removeItem('redirectAfterLogin');
      
      addToast('Başarıyla çıkış yaptınız.', 'success');
      
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [addToast]);

  // Update user profile
  const updateProfile = useCallback(async (profileData) => {
    try {
      const updatedProfile = await userApi.updateProfile(profileData);
      setUser(prevUser => ({ ...prevUser, ...updatedProfile }));
      addToast('Profiliniz başarıyla güncellendi!', 'success');
      return { success: true, user: updatedProfile };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Profil güncellenemedi. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Change password
  const changePassword = useCallback(async (passwordData) => {
    try {
      await userApi.changePassword(passwordData);
      addToast('Şifreniz başarıyla değiştirildi!', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Şifre değiştirilemedi. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Refresh user profile
  const refreshProfile = useCallback(async (force = false) => {
    try {
      if (isAuthenticated && (force || !user)) {
        // Prevent duplicate requests
        if (profileRequestRef.current) {
          return await profileRequestRef.current;
        }
        
        profileRequestRef.current = userApi.getProfile();
        const profile = await profileRequestRef.current;
        profileRequestRef.current = null;
        
        setUser(profile);
        return profile;
      }
      return user;
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      profileRequestRef.current = null;
      return null;
    }
  }, [isAuthenticated, user]);

  // Verify email
  const verifyEmail = useCallback(async (token) => {
    try {
      setLoading(true);
      await authApi.verifyEmail(token);
      
      // Refresh user profile to update email verification status
      if (isAuthenticated) {
        await refreshProfile(true);
      }
      
      addToast('E-posta adresiniz başarıyla doğrulandı!', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'E-posta doğrulama başarısız oldu. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [addToast, isAuthenticated, refreshProfile]);

  // Resend verification email
  const resendVerification = useCallback(async (email) => {
    try {
      await authApi.resendVerification(email);
      addToast('Doğrulama e-postası gönderildi! Lütfen gelen kutunuzu kontrol edin.', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Doğrulama e-postası gönderilemedi. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Request password reset
  const forgotPassword = useCallback(async (email) => {
    try {
      await authApi.forgotPassword(email);
      addToast('Şifre sıfırlama e-postası gönderildi! Lütfen gelen kutunuzu kontrol edin.', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Şifre sıfırlama e-postası gönderilemedi. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Reset password
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      await authApi.resetPassword(token, newPassword);
      addToast('Şifreniz başarıyla sıfırlandı! Artık yeni şifrenizle giriş yapabilirsiniz.', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Şifre sıfırlama başarısız oldu. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Update marketing preferences
  const updateMarketingPreferences = useCallback(async (preferences) => {
    try {
      await userApi.updateMarketingPreferences(preferences);
      
      // Update user state with new preferences
      setUser(prevUser => ({ 
        ...prevUser, 
        acceptsMarketing: preferences.acceptsMarketing,
        acceptsSms: preferences.acceptsSms 
      }));
      
      addToast('Pazarlama tercihleri başarıyla güncellendi!', 'success');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Pazarlama tercihleri güncellenemedi. Lütfen tekrar deneyin.';
      addToast(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [addToast]);

  // Check if user has permission for certain actions
  const hasPermission = useCallback((permission) => {
    if (!isAuthenticated || !user) return false;
    
    switch (permission) {
      case 'verified_email':
        return user.emailVerified;
      case 'complete_profile':
        return user.firstName && user.lastName && user.phoneNumber;
      default:
        return true;
    }
  }, [isAuthenticated, user]);

  const contextValue = {
    // State
    user,
    isAuthenticated,
    loading,
    initializing,

    // Authentication functions
    login,
    register,
    logout,
    
    // Profile management
    updateProfile,
    changePassword,
    updateMarketingPreferences,
    refreshProfile,
    
    // Email verification
    verifyEmail,
    resendVerification,
    
    // Password reset
    forgotPassword,
    resetPassword,
    
    // Utility functions
    hasPermission,
    
    // Computed values
    isEmailVerified: user?.emailVerified || false,
    fullName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    displayName: user?.firstName || user?.email || '',
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};